/**
 * Home, chapter 03 — the factory, the back of the street.
 *
 * A travelling shot: the press is a trolley on an overhead rail and the
 * camera follows it while the belt runs underneath. Raw code rides the
 * belt, goes under the press and comes out as a shipped bundle; every
 * crate links to its repository. The markup is the server's
 * (templates/site/sections/home/factory.html.twig).
 *
 * @module sections/home/factory
 */

import { REDUCED_MOTION } from '../../config.js';
import { $, $$, padCount } from '../../lib/dom.js';
import { ScrollTrigger, gsap } from '../../lib/gsap.js';

/**
 * Easter egg: on every visit one crate, drawn at random, is legendary — a
 * rare drop. It looks like the others until it comes out of the press:
 * golden tape, a glow, a "Legendary" stamp and a shower of sparks.
 *
 * @param {HTMLElement[]} crates
 * @returns {HTMLElement}
 */
function makeLegendary(crates) {
    const crate = crates[Math.floor(Math.random() * crates.length)];
    const stamp = $('.crate__stamp', crate);

    crate.classList.add('crate--legendary');

    if (stamp) {
        stamp.textContent = '★ Legendary';
    }

    return crate;
}

// The strike of the press, in seconds: the head comes down, then back up.
const STRIKE = { down: 0.12, up: 0.45 };

/**
 * The camera follows the press; rollers, wheels and gears read --spin, the
 * floor reads --travel. Every crate that reaches the middle of the screen
 * goes under the press and comes out shipped; backwards, it turns raw again.
 *
 * The press is a trolley: it strikes a crate a little before the crate
 * reaches it — as early as the belt runs fast — and rolls along its rail
 * while the head comes down, so the head always lands on the middle of the
 * crate, whatever the speed of the scroll. A crate already out of reach is
 * shipped without a strike rather than struck beside.
 *
 * @param {HTMLElement} factory
 */
export function animate(factory) {
    const track = $('[data-strip-track]', factory);
    const crates = $$('[data-crate]', factory);
    const press = $('.factory__press', factory);
    const rod = $('[data-press-rod]', factory);
    const head = $('[data-press-head]', factory);
    const shipped = $('[data-shipped]', factory);

    if (!track || !crates.length) {
        return;
    }

    const count = (value) => {
        if (shipped) {
            shipped.textContent = padCount(value);
        }
    };

    const legendary = makeLegendary(crates);

    // Reduced motion: everything is already shipped, the belt is a strip
    // you slide by hand.
    if (REDUCED_MOTION) {
        crates.forEach((crate) => crate.classList.add('is-done'));
        count(crates.length);
        return;
    }

    // How far the head travels to touch a crate, and how far the trolley
    // may roll to meet one (half the space between two crates): measured,
    // since both are sized from the viewport.
    let reach = 0;
    let range = 0;

    const measure = () => {
        range = (crates.length > 1 ? crates[1].offsetLeft - crates[0].offsetLeft : crates[0].offsetWidth) / 2;

        if (!rod || !head) {
            return;
        }

        // The crates may still wait for their entrance (lifted by a GSAP y):
        // measured where they will rest.
        const lifted = Number(gsap.getProperty(crates[0], 'y')) || 0;

        gsap.set(rod, { y: 0 });
        reach = Math.max(0, crates[0].getBoundingClientRect().top - lifted - head.getBoundingClientRect().bottom);
    };

    // The press throws sparks when the legendary crate comes out.
    const sparks = () => {
        if (!press || !head) {
            return;
        }

        const origin = head.getBoundingClientRect();
        const frame = press.getBoundingClientRect();

        for (let index = 0; index < 18; index++) {
            const spark = document.createElement('span');
            spark.className = 'factory__spark';
            spark.style.left = `${origin.left - frame.left + origin.width / 2}px`;
            spark.style.top = `${origin.bottom - frame.top + reach}px`;
            press.append(spark);

            const angle = (Math.PI * index) / 17;
            const distance = gsap.utils.random(50, 140);

            gsap.fromTo(
                spark,
                { x: 0, y: 0, scale: 1, opacity: 1 },
                {
                    x: -Math.cos(angle) * distance,
                    y: -Math.sin(angle) * distance * 0.8,
                    scale: 0.2,
                    opacity: 0,
                    duration: gsap.utils.random(0.5, 0.9),
                    ease: 'power2.out',
                    delay: 0.1,
                    onComplete: () => spark.remove(),
                },
            );
        }
    };

    // Where a crate's middle stands, from the middle of the screen.
    const offset = (crate, x) => crate.offsetLeft + crate.offsetWidth / 2 + x - window.innerWidth / 2;

    // Where the trolley stands on its rail, from the middle (--trolley: the
    // stylesheet keeps the press centred with `translate`, which a GSAP `x`
    // would overwrite).
    const trolley = { x: 0 };

    const setTrolley = (value) => {
        trolley.x = value;
        press?.style.setProperty('--trolley', `${value.toFixed(1)}px`);
    };

    // The strike under way: the crate, where the trolley started, and the
    // descent of the head — the trolley meets the crate as the head lands.
    let strike = null;

    const ship = (crate) => {
        crate.classList.add('is-done');

        if (crate === legendary) {
            sparks();
        }
    };

    const strikeAt = (crate) => {
        if (!press || !rod) {
            ship(crate);
            return;
        }

        gsap.killTweensOf(trolley);

        strike = {
            crate,
            from: trolley.x,
            descent: gsap.to(rod, { y: reach, duration: STRIKE.down, ease: 'power3.in', overwrite: 'auto' }),
        };
    };

    // Head up, trolley back above the middle.
    const release = () => {
        strike = null;
        gsap.to(rod, { y: 0, duration: STRIKE.up, ease: 'power2.out', overwrite: 'auto' });
        gsap.to(trolley, {
            x: 0,
            duration: STRIKE.up + 0.25,
            ease: 'power2.inOut',
            onUpdate: () => setTrolley(trolley.x),
        });
    };

    // Runs after GSAP has drawn the frame, belt included. The trolley rolls
    // toward where the crate will stand when the head lands, at the pace of
    // this frame, and stands exactly on it at the landing. When that point
    // leaves the trolley's reach, the head goes back up before it touches
    // anything: a crate already past is shipped, one still on its way will
    // be struck when it comes.
    const follow = (x, speed) => {
        if (!strike) {
            return;
        }

        const { crate, from, descent } = strike;
        const position = offset(crate, x);
        const progress = descent.progress();
        const landing = position - speed * (1 - progress) * STRIKE.down;

        if (Math.abs(landing) > range) {
            descent.kill();
            release();

            if (landing < 0) {
                ship(crate);
            }
        } else if (progress < 1) {
            setTrolley(from + (landing - from) * progress);
        } else {
            setTrolley(position);
            release();
            ship(crate);
        }
    };

    let last = null;
    let done = -1;

    gsap.ticker.add((time, deltaTime) => {
        const x = Number(gsap.getProperty(track, 'x')) || 0;
        // The pace of the line, forward, in px per second.
        const speed = last === null ? 0 : (Math.max(0, last - x) / Math.max(deltaTime, 1)) * 1000;

        // Every frame, the belt running or not.
        follow(x, speed);

        if (x === last) {
            return;
        }

        const forward = last !== null && x < last;
        // How far the belt runs while the head comes down: strike that much early.
        const lead = speed * STRIKE.down;
        let passed = 0;
        let next = null;

        last = x;
        factory.style.setProperty('--spin', (-x * 0.45).toFixed(1));
        // The floor runs under the camera at the pace of the line.
        factory.style.setProperty('--travel', `${x.toFixed(1)}px`);

        crates.forEach((crate) => {
            const position = offset(crate, x);
            const isDone = crate.classList.contains('is-done');

            if (position < 0) {
                passed++;
            }

            if (!forward) {
                // Backwards, a crate back past the press turns raw again.
                if (isDone && position >= 0 && crate !== strike?.crate) {
                    crate.classList.remove('is-done');
                }

                return;
            }

            if (isDone || crate === strike?.crate || position > lead) {
                return;
            }

            if (position - lead < -range) {
                // Out of the trolley's reach by the time the head lands:
                // shipped, never struck beside.
                ship(crate);
            } else if (!next || position > offset(next, x)) {
                next = crate;
            }
        });

        // A new strike waits for the head to be at least halfway back up: the
        // head never skids along the belt from one crate to the next.
        if (next && !strike && (Number(gsap.getProperty(rod, 'y')) || 0) <= reach / 2) {
            strikeAt(next);
        }

        if (passed !== done) {
            done = passed;
            count(passed);
        }
    });

    measure();
    ScrollTrigger.addEventListener('refresh', measure);
}
